export const ERROR_MESSAGES = {
  FILE_TOO_LARGE: 'O arquivo excede o limite de 20MB',
  UNSUPPORTED_FILE_TYPE:
    'Tipo de arquivo não suportado. Aceitos: PDF, JPEG, PNG, WebP',

  NETWORK_ERROR: 'Erro de rede. Verifique sua conexão e tente novamente',
  TIMEOUT_ERROR: 'A requisição expirou. Tente novamente',

  UNAUTHORIZED: 'Autenticação necessária. Faça login novamente',
  FORBIDDEN: 'Você não tem permissão para realizar esta ação',

  URL_EXPIRED: 'A URL de upload expirou. Tente novamente',
  URL_GENERATION_FAILED: 'Falha ao gerar URL de upload. Tente novamente',

  UPLOAD_FAILED: 'Falha no upload após múltiplas tentativas',
  S3_UPLOAD_ERROR: 'Erro ao enviar arquivo para o servidor',
  INVALID_CONTENT_TYPE: 'Tipo de conteúdo inválido para o arquivo',

  NOTIFICATION_FAILED: 'Arquivo enviado, mas falha ao notificar o sistema',
  PROCESSING_QUEUE_ERROR: 'Erro ao enfileirar documento para processamento',

  INVALID_REQUEST: 'Parâmetros de requisição inválidos',
  SERVER_ERROR: 'Erro interno do servidor. Tente novamente mais tarde',
  UNKNOWN_ERROR: 'Erro desconhecido. Entre em contato com o suporte',

  UPLOAD_CANCELLED: 'Upload cancelado pelo usuário'
} as const

export type ErrorMessageKey = keyof typeof ERROR_MESSAGES

export const HTTP_STATUS_ERROR_MAP: Record<number, ErrorMessageKey> = {
  400: 'INVALID_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'URL_EXPIRED',
  415: 'UNSUPPORTED_FILE_TYPE',
  500: 'SERVER_ERROR',
  502: 'SERVER_ERROR',
  503: 'SERVER_ERROR',
  504: 'TIMEOUT_ERROR'
}

export const UploadStage = {
  VALIDATION: 'validation',
  REQUEST_URL: 'request_url',
  S3_UPLOAD: 's3_upload',
  NOTIFICATION: 'notification'
} as const

export type UploadStage = typeof UploadStage[keyof typeof UploadStage]

export const STAGE_ERROR_MAP: Record<
  UploadStage,
  Record<string, ErrorMessageKey>
> = {
  [UploadStage.VALIDATION]: {
    file_too_large: 'FILE_TOO_LARGE',
    unsupported_type: 'UNSUPPORTED_FILE_TYPE'
  },
  [UploadStage.REQUEST_URL]: {
    network: 'NETWORK_ERROR',
    unauthorized: 'UNAUTHORIZED',
    server: 'URL_GENERATION_FAILED'
  },
  [UploadStage.S3_UPLOAD]: {
    network: 'NETWORK_ERROR',
    expired: 'URL_EXPIRED',
    invalid_content: 'INVALID_CONTENT_TYPE',
    upload_failed: 'S3_UPLOAD_ERROR'
  },
  [UploadStage.NOTIFICATION]: {
    network: 'NETWORK_ERROR',
    unauthorized: 'UNAUTHORIZED',
    server: 'NOTIFICATION_FAILED'
  }
}

export function getErrorMessage(errorKey: ErrorMessageKey): string {
  return ERROR_MESSAGES[errorKey]
}

export function getErrorMessageFromStatus(statusCode: number): string {
  const errorKey = HTTP_STATUS_ERROR_MAP[statusCode] || 'UNKNOWN_ERROR'
  return ERROR_MESSAGES[errorKey]
}

export function getStageErrorMessage(
  stage: UploadStage,
  errorType: string
): string {
  const stageErrors = STAGE_ERROR_MAP[stage]
  const errorKey = stageErrors[errorType] || 'UNKNOWN_ERROR'
  return ERROR_MESSAGES[errorKey]
}

export function isNetworkError(error: any): boolean {
  return (
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ENOTFOUND' ||
    error?.code === 'ECONNREFUSED' ||
    error?.message?.includes('Network Error') ||
    error?.message?.includes('timeout')
  )
}

export function isRetryableError(error: any, stage: UploadStage): boolean {
  if (isNetworkError(error)) {
    return true
  }
  const retryableStatusCodes = [408, 429, 500, 502, 503, 504]
  if (
    error?.response?.status &&
    retryableStatusCodes.includes(error.response.status)
  ) {
    return true
  }
  if (stage === UploadStage.S3_UPLOAD && error?.response?.status === 403) {
    return true
  }
  return false
}

export function extractErrorMessage(error: any): string {
  if (error?.response?.data?.message) {
    return error.response.data.message
  }
  if (error?.response?.status) {
    return getErrorMessageFromStatus(error.response.status)
  }
  if (error?.message) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return ERROR_MESSAGES.UNKNOWN_ERROR
}
